<template>
  <v-container fluid justify-start>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredKiosks"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      class="elevation-2"
      >

      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>
      
        <!-- Header Toolbar -->
        <v-toolbar dense dark flat :color="appColor_Medium">
          
          <app-back-button v-bind:color="appColor_Dark" />

          <v-toolbar-title>Unupdated Abbys</v-toolbar-title>

        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">
          <!-- Facility -->
          <v-col cols="12" md="4">
            <v-combobox
              ref="cboFacilityFilter"
              v-model="selectedFacility"
              item-value="id"
              item-text="name"
              :items="facilitiesForFilter"
              @change="facilityFilterChanged"
              label="Facility"
              placeholder="Facility"
              :disabled="isLoading"
            />
          </v-col>

          <!-- Search -->
          <v-col cols="12" md="8">
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
              :disabled="isLoading"
              clearable
              class="ml-3"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.facilityId="{ item }">
        <span class="text-left">{{ getFacilityName(item.facilityId) }}</span>
      </template>

      <template v-slot:item.mediaSyncDate="{ item }">
        <span class="mr-4">
          <v-icon v-if="isOnline(item.mediaSyncDate)" :color="appColor_Selected">done</v-icon>
          <v-icon v-else :color="'grey lighten-2'">cancel</v-icon>
        </span>
      </template>

    </v-data-table>

  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { mapGetters } from 'vuex'
import { AppColor, UserIds } from '@/config.js'
import AppBackButton from '@/components/App/AppBackButton'

export default {
  name: 'UnupdatedAbbys',
  components: {
    AppBackButton
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Selected: AppColor.Selected,
      appColor_Progress: AppColor.Progress,

      isLoading: true,
      search: '',

      headers: [
        {
          text: 'Name',
          value: 'name',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        },
        {
          text: 'Description',
          value: 'description',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          text: 'Facility',
          value: 'facilityId',
          align: 'left',
          width: '25%',
          class: 'pr-0'
        },
        {
          text: 'Online',
          value: 'mediaSyncDate',
          align: 'center',
          width: '10%'
        },        
        {
          text: 'TeamViewer ID',
          value: 'teamviewerId',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        },
        {
          text: 'TeamViewer Pwd',
          value: 'teamviewerPassword',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        }
      ],
      kiosks: [],

      facilities: [],
      facilitiesForFilter: [],
      facilityId: 0,
      selectedFacility: { id: 0, name: '(No Facility)' },
      noFacility: { id: 0, name: '(No Facility)' },

      // temporary (for tammy)
      tammy: UserIds.Tammy
    }
  },
  computed: {
    ...mapGetters(['profile']),
    noDataText () {
      if (this.isLoading) return 'One moment...'

      return this.kiosks.length === 0
        ? 'No kiosks found'
        : ''
    },
    toolbarColor () {
      return this.appColor_Medium
    },
    filteredKiosks() {
      if (this.kiosks === null) return []
      if (this.facilities === null) return []

      const self = this
      const facilityId = !this.isNullOrUndefined(self.selectedFacility.id)
        ? self.selectedFacility.id
        : 0

      return self.kiosks.filter((k) => {
        return (
          (k.facilityId === facilityId || facilityId === 0)
        )
      })
    },
    isTammy () {
      if (this.isNullOrUndefined(this.profile)) return false
      return this.tammy.includes(this.profile.id)
    }
  },
  methods: {
    loadData() {
      const self = this
      self.isLoading = true
      if (!self.isTammy) {
        self.goBack()
      } else {
        appService.getUnupdatedAbbys().then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            self.kiosks = data.kiosks
              .filter(k => ![1, 115, 118, 127, 133, 134, 135, 136, 193, 229, 364, 391, 403, 404].includes(k.id)
                && k.teamviewerId !== null)

            appService.getFacilities().then(data => {
              if (data.errorSeverity > 0) {
                self.handleError(data)
              } else {
                  self.facilities = []
                  self.facilities.push({ id: 0, name: '(No Facility)', code: '(None)' })
                  data.facilities.forEach((f) => {
                    f.name = self.getExtendedFacilityName(f)
                    self.facilities.push(f)
                  })

                  self.facilitiesForFilter = []
                  self.facilitiesForFilter.push({ id: 0, name: '(All)', code: '(All)' })
                  data.facilities.forEach((f) => {
                    self.facilitiesForFilter.push(f)
                  })
              }
            }).catch(e => { self.handleCriticalError(e) })
          }
          self.isLoading = false
        }).catch(e => { self.handleCriticalError(e) }) 
      }
    },

    getFacilityName (id) {
      const facilities = this.facilities.filter((f) => {
        return f.id === id
      })
      return facilities.length > 0 ? facilities[0].name : null
    },

    getExtendedFacilityName (f) {
      if (f === null) return null
      return f.geographicalDescription !== null
        ? `${f.name} (${f.geographicalDescription})`
        : f.name
    },
    
    isOnline (mediaSyncDate) {
      const today = new Date()
      const dd = String(today.getDate()).padStart(2, '0')
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const yyyy = today.getFullYear()
      const todayDate = this.getDate(mm + '/' + dd + '/' + yyyy)
      const syncDate = this.getDate(mediaSyncDate)

      return this.getDate(syncDate) === todayDate
    },

    getDate (date) {
      if (!this.isNullOrUndefined(date)) {
        if (date.length > 0) {
          return this.$moment(date).format('MMM-DD-YYYY')
        }
      }
      return null
    },

    getTime (date) {
      if (date !== null) {
        if (date.length > 0) {
          return this.$moment(date).format('hh:mm:ss A')
        }
      }
      return null
    },

    customFilter (value, search, item) {
      const description = item.description ?? ''

      var facilityName = ''
      var facilityLocation = ''

      if (item.facility !== null) {
        facilityName = item.facility.name

        if (item.facility.geographicalDescription !== null) {
          facilityLocation = item.facility.geographicalDescription
        }
      }

      return (
        value != null &&
        search != null &&
        (item.name.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        description.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        facilityName.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        facilityLocation.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1)
      )
    },

    handleError (result) {
      this.$error.show({
        title:
          result.errorSeverity === 0 ? 'Validation Failed' : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    },

    // events
    facilityFilterChanged () {
      if (this.isNullOrUndefined(this.selectedFacility.id)) {
        const cboFacilityFilter = this.$refs.cboFacilityFilter
        this.selectedFacility = { id: 0, name: '(All)' }
        cboFacilityFilter.internalValue = this.selectedFacility.name
      }
    }
  },
  created() {
    this.loadData()
  }
}
</script>
